import { Info } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import type { ScaleThreshold } from '@/types/price-rules';

// Cada faixa do template compara o PMR atual (GAM) com a meta de PMR da regra:
// a distancia, em pontos percentuais, escolhe quanto o price sobe ou desce.
// A regra de calculo mora no ms-price-rules (price_rule_service.simulate_new_rule).
export function formatThreshold(t: ScaleThreshold) {
    return `Δ PMR ≥ ${t.difference_percentage} p.p. → price ±${t.scale_percentage}%`;
}

interface Props {
    thresholds?: ScaleThreshold[];
}

export default function ScaleThresholdsInfo({ thresholds }: Props) {
    const sorted = thresholds ? [...thresholds].sort((a, b) => a.difference_percentage - b.difference_percentage) : [];

    return (
        <Popover>
            <PopoverTrigger asChild>
                <span
                    role="button"
                    tabIndex={0}
                    aria-label="O que é o template de sync"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-purple-600 hover:bg-purple-50 cursor-pointer"
                >
                    <Info className="h-4 w-4" />
                </span>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-sm" align="start" onClick={(e) => e.stopPropagation()}>
                <p className="font-semibold text-gray-900">Template de Sync</p>
                <p className="mt-1 text-gray-600">
                    Define quanto o price da regra muda a cada sincronização com o GAM.
                </p>
                <p className="mt-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">O que é comparado</p>
                <p className="mt-1 text-gray-600">
                    O <b>PMR atual</b> do GAM (impressões ÷ requisições elegíveis) contra a <b>Meta de PMR</b> da
                    regra (passo 3). A diferença entre os dois, em pontos percentuais, é o <b>Δ PMR</b>.
                </p>
                <ul className="mt-2 space-y-1 text-gray-600">
                    <li>• PMR <b className="text-red-600">abaixo</b> da meta → price <b className="text-red-600">desce</b></li>
                    <li>• PMR <b className="text-emerald-600">acima</b> da meta → price <b className="text-emerald-600">sobe</b> (se o eCPM sustentar)</li>
                </ul>
                <p className="mt-2 text-gray-600">Vale a maior faixa que o Δ PMR atingir; abaixo da primeira, o price não muda.</p>
                {sorted.length > 0 ? (
                    <div className="mt-3 space-y-1">
                        {sorted.map((t, i) => (
                            <div key={i} className="px-2 py-1 bg-gray-50 rounded text-xs text-gray-700">{formatThreshold(t)}</div>
                        ))}
                    </div>
                ) : (
                    <p className="mt-3 text-xs text-gray-500">
                        Ex.: meta 40%, PMR atual 30% → Δ PMR = 10 p.p. Com a faixa “Δ PMR ≥ 10 p.p. → price ±5%”, o price cai 5%.
                    </p>
                )}
            </PopoverContent>
        </Popover>
    );
}
